import React, { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "./Footer";
import "../css/contactus.scss";
import Header from "./Header";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaComments,
} from "react-icons/fa";

function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject) {
      newErrors.subject = "Please select a subject";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    setTimeout(() => {
      setShowSuccess(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
      setIsSubmitting(false);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }, 1000);
  };

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="info-page contact">
      <Header />

      <div className="info-page__breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Contact Us</span>
      </div>

      <div className="info-page__content">
        <div className="info-page__main">
          <h1 className="info-page__title">Contact Us</h1>
          <p className="info-page__intro">
            We'd love to hear from you! Whether you have a question about our
            products, need help with an order, or just want to say hello, we're
            here for you and your pets.
          </p>

          {showSuccess && (
            <div className="success-message">
              <div className="success-icon">
                <FaComments />
              </div>
              <h3>Message Sent Successfully!</h3>
              <p>
                Thank you for contacting us. Our team will get back to you
                within 1-2 business days.
              </p>
            </div>
          )}

          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    <FaUser className="label-icon" />
                    First Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className={`form-input ${errors.firstName ? "error" : ""}`}
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    <FaUser className="label-icon" />
                    Last Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className={`form-input ${errors.lastName ? "error" : ""}`}
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <FaEnvelope className="label-icon" />
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-input ${errors.email ? "error" : ""}`}
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    <FaPhone className="label-icon" />
                    Phone Number <span className="optional">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  Subject <span className="required">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  className={`form-select ${errors.subject ? "error" : ""}`}
                  value={formData.subject}
                  onChange={handleInputChange}
                >
                  <option value="">Select a subject</option>
                  <option value="order">Order Inquiry</option>
                  <option value="product">Product Question</option>
                  <option value="returns">Returns & Exchanges</option>
                  <option value="feedback">Feedback & Suggestions</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="other">Other</option>
                </select>
                {errors.subject && (
                  <span className="error-message">{errors.subject}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  <FaComments className="label-icon" />
                  Message <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  className={`form-textarea ${errors.message ? "error" : ""}`}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="How can we help you and your furry friend today?"
                  rows="6"
                />
                {errors.message && (
                  <span className="error-message">{errors.message}</span>
                )}
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="btn-loading">Sending...</span>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          </div>
        </div>

        <aside className="info-page__sidebar">
          <div className="sidebar-card">
            <h3 className="sidebar-card__title">Get in Touch</h3>
            <p className="sidebar-card__text">
              Our customer service team is ready to help you and your pets.
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <FaPhone className="contact-icon" />
                <div className="contact-text">
                  <span className="contact-label">Phone</span>
                  <a href="tel:8553007529" className="contact-value">
                    855.300.7529
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <FaEnvelope className="contact-icon" />
                <div className="contact-text">
                  <span className="contact-label">Email</span>
                  <a
                    href="mailto:split@petmorvanto.club"
                    className="contact-value"
                  >
                    split@petmorvanto.club
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <FaClock className="contact-icon" />
                <div className="contact-text">
                  <span className="contact-label">Hours</span>
                  <span className="contact-value">Mon-Fri: 9AM-5PM EST</span>
                </div>
              </div>
            </div>
          </div>

          <div className="sidebar-card">
            <h3 className="sidebar-card__title">Quick Help</h3>
            <ul className="sidebar-links">
              <li>
                <Link to="/faq" onClick={toTop}>
                  Frequently Asked Questions
                </Link>
              </li>
              <li>
                <Link to="/shipping" onClick={toTop}>
                  Shipping Information
                </Link>
              </li>
              <li>
                <Link to="/returns" onClick={toTop}>
                  Return Policy
                </Link>
              </li>
              <li>
                <Link to="/track-order" onClick={toTop}>
                  Track Your Order
                </Link>
              </li>
            </ul>
          </div>

          <div className="sidebar-card">
            <h3 className="sidebar-card__title">Our Location</h3>
            <div className="location-info">
              <div className="location-item">
                <FaMapMarkerAlt className="location-icon" />
                <div className="location-text">
                  <p>9750 Royal Lane Dallas, TX 75231 United States</p>
                </div>
              </div>
            </div>
            <p className="location-note">
              While our toys are made with love, our headquarters is where the
              magic happens for your furry friends.
            </p>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  );
}

export default Contact;
